/**
 * RegulatoryNotice.tsx
 * ECG.WATCH · Componentes públicos de aviso regulatorio
 *
 * Versión sintética del Marco Operativo Pre-Comercial v1.0
 * (ECGW-MARCO-PRECOM-v1.0) para superficies públicas.
 *
 * Exporta dos componentes:
 *   - RegulatoryBanner:     bloque editorial dentro de Landing / LandingEn
 *   - RegulatoryNoticePage: página completa /aviso-regulatorio | /en/regulatory-notice
 *
 * El tercer nivel (footer permanente) lo cumple PublicFooter — ver patch del
 * disclaimer en ese componente. Acá NO duplicamos esa lógica.
 *
 * Stack alineado con la landing pública: inline styles + tokens del módulo
 * publicTokens, fuentes Fraunces (headings) e Instrument Sans (body)
 * cargadas globalmente, JetBrains Mono para eyebrows uppercase.
 *
 * Bilingüe ES/EN (mismo patrón que PublicNav y PublicFooter).
 */

import { Link } from 'react-router-dom';
import { PublicNav } from './PublicNav';
import { PublicFooter } from './PublicFooter';
import {
  PRIMARY,
  BG,
  BG_DARKER,
  BG_CARD,
  BORDER,
  TEXT,
  TEXT_DIM,
  TEXT_FAINT,
} from '../theme/publicTokens';

type Lang = 'es' | 'en';

// ─────────────────────────────────────────────────────────────────
// Strings bilingues
// ─────────────────────────────────────────────────────────────────

const STRINGS: Record<Lang, {
  // Banner
  bannerEyebrow: string;
  bannerHeading: string;
  bannerP1: string;
  bannerP2: string;
  bannerLink: string;

  // Page
  pageEyebrow: string;
  pageTitle: string;
  pageSubtitle: string;
  pageMeta: string;

  s1Title: string;
  s1Body: string[];

  s2Title: string;
  s2Body: string[];

  s3Title: string;
  s3Body: string[];

  s4Title: string;
  s4Body: string[];

  s5Title: string;
  s5Body: string[];

  s6Title: string;
  s6Body: string[];

  s7Title: string;
  s7Body: string[];

  s8Title: string;
  s8Body: string[];

  docMeta: string;
  backHome: string;
}> = {
  es: {
    bannerEyebrow: 'Estado del producto',
    bannerHeading: 'Transparencia regulatoria',
    bannerP1:
      'ECG.WATCH se encuentra en fase de desarrollo activo. Los algoritmos que computa están validados internamente por médico matriculado bajo el Protocolo de Validación Clínica ECG.WATCH v1.0, pero el producto no cuenta con registro como dispositivo médico ante ANMAT.',
    bannerP2:
      'Hoy se opera bajo supervisión clínica directa del médico tratante, en marco de investigación y desarrollo. ECG.WATCH no constituye diagnóstico autónomo, no reemplaza la consulta médica presencial, y no es apto para situaciones de emergencia.',
    bannerLink: 'Leer aviso regulatorio completo',

    pageEyebrow: 'Aviso regulatorio',
    pageTitle: 'Estado regulatorio de ECG.WATCH',
    pageSubtitle:
      'Síntesis pública del marco bajo el cual la plataforma se opera durante su fase pre-comercial.',
    pageMeta: 'Vigente desde el 22 de mayo de 2026 · Versión 1.0',

    s1Title: '1. Qué es ECG.WATCH',
    s1Body: [
      'ECG.WATCH es una plataforma de software para procesamiento, cuantificación y revisión clínica de electrocardiogramas de una sola derivación adquiridos mediante Apple Watch. Convierte el trazado nativo de Apple Watch en un dataset clínico navegable, calcula métricas estándar (frecuencia cardíaca, variabilidad cardíaca, intervalos PR, QRS, QT, QTc) y las pone a disposición del médico tratante para su revisión.',
      'Cada algoritmo que la plataforma utiliza se valida contra dataset clínico de referencia, se versiona, y se firma por médico matriculado conforme al Protocolo de Validación Clínica ECG.WATCH v1.0. La trazabilidad de cada resultado al algoritmo, a su versión, y al validador firmante constituye el diferencial técnico-metodológico del proyecto.',
    ],

    s2Title: '2. Qué NO es ECG.WATCH',
    s2Body: [
      'ECG.WATCH no es un dispositivo médico aprobado. No realiza diagnóstico autónomo. No es un sistema de detección automática de arritmias para uso del paciente. No reemplaza la consulta médica presencial ni los estudios cardiológicos estándar (electrocardiograma de 12 derivaciones, Holter, ergometría). No es apto para situaciones de emergencia clínica.',
      'Las salidas algorítmicas tienen carácter de soporte clínico. Toda interpretación corresponde al médico tratante en ejercicio de su criterio profesional.',
    ],

    s3Title: '3. Estado regulatorio actual',
    s3Body: [
      'ECG.WATCH se encuentra en fase de desarrollo activo. No cuenta con registro como producto médico ante la Administración Nacional de Medicamentos, Alimentos y Tecnología Médica (ANMAT) de la República Argentina, ni ante ninguna otra autoridad sanitaria.',
      'Su categorización regulatoria provisional, según el framework FDA / IMDRF para Software como Dispositivo Médico (SaMD) adoptado como referencia internacional, es Clase II — decision support no autónomo. Esta categorización es hipótesis del proyecto, sujeta a evaluación regulatoria formal por las autoridades correspondientes.',
      'El proyecto avanza en paralelo el track regulatorio formal, con horizonte estimado de presentación de legajo ANMAT entre 12 y 18 meses desde la fecha de este aviso.',
    ],

    s4Title: '4. Cómo se utiliza hoy',
    s4Body: [
      'El uso vigente de ECG.WATCH se enmarca exclusivamente en supervisión clínica directa por médico matriculado, con consentimiento informado escrito del paciente y trazabilidad completa de cada acto sobre la plataforma. La responsabilidad clínica de toda decisión basada en outputs del software recae en el médico tratante, conforme a la Ley 17.132 de ejercicio de la medicina.',
      'El producto no se comercializa. No se factura por su uso. No se publicita como dispositivo médico aprobado ni se distribuye fuera de los marcos descriptos en este aviso.',
    ],

    s5Title: '5. Información para profesionales médicos',
    s5Body: [
      'El interés profesional en ECG.WATCH se canaliza a través de instancias formales: convenios de investigación, participación en estudios prospectivos bajo aprobación de Comité de Ética, o conversaciones de validación clínica con el equipo. Cualquier uso del producto sobre pacientes propios fuera del marco descripto en este aviso debe coordinarse previamente con el equipo de ECG.WATCH.',
      'Para profesionales interesados: hola@ecgwatch.online.',
    ],

    s6Title: '6. Información para pacientes',
    s6Body: [
      'Si su médico le propone utilizar ECG.WATCH como herramienta adicional en su seguimiento, le recomendamos leer el documento de consentimiento informado completo, plantear todas las preguntas que tenga, y comprender claramente que el software no reemplaza la consulta médica ni los estudios estándar.',
      'En caso de síntomas de emergencia (dolor torácico, palpitaciones intensas, pérdida de conciencia, dificultad respiratoria severa) NO utilice ECG.WATCH. Llame al 107 (emergencias médicas) o concurra al servicio de urgencias más cercano.',
    ],

    s7Title: '7. Roadmap regulatorio',
    s7Body: [
      'El proyecto tiene en preparación el legajo regulatorio para presentación ante ANMAT, con horizonte estimado de 12 a 18 meses. Las etapas previstas incluyen: constitución de entidad legal titular del registro, designación de Director Técnico, implementación de Sistema de Gestión de Calidad conforme a ISO 13485, ejecución del Protocolo de Estudio Clínico EP-Pilot v1.1 (estudio prospectivo observacional N=100), consolidación del Clinical Evaluation Report, y presentación del legajo técnico completo.',
      'Las novedades regulatorias del proyecto se publican en esta misma página a medida que se concretan.',
    ],

    s8Title: '8. Contacto regulatorio',
    s8Body: [
      'Consultas sobre el estado regulatorio del producto, solicitudes de información para auditoría, o requerimientos formales de autoridades sanitarias: hola@ecgwatch.online.',
      'Política de Privacidad vigente en /privacidad. Consentimiento informado en /consentimiento.',
    ],

    docMeta:
      'Documento de referencia interna: Marco Operativo Pre-Comercial ECG.WATCH v1.0 (ECGW-MARCO-PRECOM-v1.0).',
    backHome: 'Volver al inicio',
  },

  en: {
    bannerEyebrow: 'Product status',
    bannerHeading: 'Regulatory transparency',
    bannerP1:
      'ECG.WATCH is in active development. The algorithms it computes are internally validated by a licensed physician under the ECG.WATCH Clinical Validation Protocol v1.0, but the product is not registered as a medical device with Argentina\u2019s ANMAT.',
    bannerP2:
      'It is currently operated under direct clinical supervision of the treating physician, within a research-and-development framework. ECG.WATCH does not provide autonomous diagnosis, does not replace in-person medical consultation, and is not suitable for emergencies.',
    bannerLink: 'Read full regulatory notice',

    pageEyebrow: 'Regulatory notice',
    pageTitle: 'ECG.WATCH regulatory status',
    pageSubtitle:
      'Public synthesis of the framework under which the platform operates during its pre-commercial phase.',
    pageMeta: 'In force since May 22, 2026 \u00B7 Version 1.0',

    s1Title: '1. What ECG.WATCH is',
    s1Body: [
      'ECG.WATCH is a software platform for processing, quantification, and clinical review of single-lead electrocardiograms acquired by Apple Watch. It converts the native Apple Watch tracing into a navigable clinical dataset, computes standard metrics (heart rate, heart rate variability, PR, QRS, QT, QTc intervals), and makes them available to the treating physician for review.',
      'Every algorithm used by the platform is validated against a clinical reference dataset, versioned, and signed by a licensed physician in accordance with the ECG.WATCH Clinical Validation Protocol v1.0. The traceability of each result back to algorithm, version, and signing validator is the technical-methodological differentiator of the project.',
    ],

    s2Title: '2. What ECG.WATCH is NOT',
    s2Body: [
      'ECG.WATCH is not an approved medical device. It does not perform autonomous diagnosis. It is not an automatic arrhythmia detection system for patient use. It does not replace in-person medical consultation or standard cardiological studies (12-lead electrocardiogram, Holter, exercise testing). It is not suitable for medical emergencies.',
      'Algorithmic outputs are clinical decision support. Interpretation always rests with the treating physician, exercising professional judgment.',
    ],

    s3Title: '3. Current regulatory status',
    s3Body: [
      'ECG.WATCH is in active development. It is not registered as a medical product with Argentina\u2019s National Administration of Drugs, Foods and Medical Technology (ANMAT), nor with any other health authority.',
      'Its provisional regulatory categorization, following the FDA / IMDRF Software as a Medical Device (SaMD) framework adopted as international reference, is Class II \u2014 non-autonomous decision support. This categorization is a project hypothesis, subject to formal regulatory evaluation by competent authorities.',
      'The project is advancing the formal regulatory track in parallel, with an estimated ANMAT dossier submission horizon of 12 to 18 months from the date of this notice.',
    ],

    s4Title: '4. How it is used today',
    s4Body: [
      'ECG.WATCH is currently used exclusively under direct clinical supervision by a licensed physician, with written informed consent of the patient and full traceability of every act on the platform. Clinical responsibility for any decision based on software outputs rests with the treating physician, in accordance with Argentina\u2019s Law 17.132 on the practice of medicine.',
      'The product is not commercialized. There is no billing for its use. It is not advertised as an approved medical device nor distributed outside the frameworks described in this notice.',
    ],

    s5Title: '5. Information for medical professionals',
    s5Body: [
      'Professional interest in ECG.WATCH is channeled through formal instances: research agreements, participation in prospective studies under Ethics Committee approval, or clinical validation conversations with the team. Any use of the product on own patients outside the framework described in this notice must be coordinated in advance with the ECG.WATCH team.',
      'For interested professionals: hola@ecgwatch.online.',
    ],

    s6Title: '6. Information for patients',
    s6Body: [
      'If your physician proposes using ECG.WATCH as an additional tool in your follow-up, we recommend reading the full informed consent document, raising any questions you may have, and clearly understanding that the software does not replace medical consultation or standard studies.',
      'In case of emergency symptoms (chest pain, intense palpitations, loss of consciousness, severe shortness of breath) DO NOT use ECG.WATCH. Contact your local emergency services or proceed to the nearest emergency room.',
    ],

    s7Title: '7. Regulatory roadmap',
    s7Body: [
      'The project is preparing the regulatory dossier for submission to ANMAT, with an estimated horizon of 12 to 18 months. Planned stages include: incorporation of legal entity holding the registration, designation of Technical Director, implementation of Quality Management System per ISO 13485, execution of the EP-Pilot v1.1 Clinical Study Protocol (prospective observational study, N=100), consolidation of the Clinical Evaluation Report, and submission of the full technical dossier.',
      'Regulatory milestones are published on this page as they materialize.',
    ],

    s8Title: '8. Regulatory contact',
    s8Body: [
      'Inquiries about the regulatory status of the product, requests for audit information, or formal requirements from health authorities: hola@ecgwatch.online.',
      'Current Privacy Policy at /privacidad. Informed consent at /consentimiento.',
    ],

    docMeta:
      'Internal reference document: ECG.WATCH Pre-Commercial Operating Framework v1.0 (ECGW-MARCO-PRECOM-v1.0).',
    backHome: 'Back to home',
  },
};

// ─────────────────────────────────────────────────────────────────
// Fuentes — sólo necesario en la página standalone (entry directa)
// Si el usuario viene desde la landing, ya están cargadas vía GlobalStyles.
// ─────────────────────────────────────────────────────────────────

function FontImport() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Instrument+Sans:wght@400;500;600&family=JetBrains+Mono:wght@300;400;500&display=swap');
    `}</style>
  );
}

// ─────────────────────────────────────────────────────────────────
// Banner — bloque editorial dentro de Landing / LandingEn
// ─────────────────────────────────────────────────────────────────

interface RegulatoryBannerProps {
  lang?: Lang;
}

export function RegulatoryBanner({ lang = 'es' }: RegulatoryBannerProps) {
  const t = STRINGS[lang];
  const linkHref = lang === 'en' ? '/en/regulatory-notice' : '/aviso-regulatorio';

  return (
    <section
      style={{
        background: BG_DARKER,
        padding: '96px 0',
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div
          className="font-mono text-xs uppercase tracking-widest"
          style={{ color: PRIMARY, marginBottom: 16 }}
        >
          {t.bannerEyebrow}
        </div>

        <h2
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 400,
            fontSize: 'clamp(2rem, 4vw, 2.75rem)',
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            color: TEXT,
            margin: '0 0 32px',
          }}
        >
          {t.bannerHeading}
        </h2>

        <div
          style={{
            borderLeft: `2px solid ${PRIMARY}`,
            paddingLeft: 28,
            maxWidth: '68ch',
          }}
        >
          <p
            style={{
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              fontSize: '1.0625rem',
              lineHeight: 1.65,
              color: TEXT_DIM,
              margin: '0 0 18px',
            }}
          >
            {t.bannerP1}
          </p>
          <p
            style={{
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              fontSize: '1.0625rem',
              lineHeight: 1.65,
              color: TEXT_DIM,
              margin: 0,
            }}
          >
            {t.bannerP2}
          </p>
        </div>

        <div style={{ marginTop: 40 }}>
          <Link
            to={linkHref}
            className="hover-lift inline-flex items-center gap-2 px-7 py-4 font-mono text-sm uppercase tracking-wider"
            style={{
              color: TEXT,
              border: `1px solid ${PRIMARY}`,
              borderRadius: 4,
              textDecoration: 'none',
              background: `${PRIMARY}0A`,
            }}
          >
            {t.bannerLink}
            <span style={{ color: PRIMARY }}>→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page completa — /aviso-regulatorio | /en/regulatory-notice
// ─────────────────────────────────────────────────────────────────

interface RegulatoryNoticePageProps {
  lang?: Lang;
}

export function RegulatoryNoticePage({ lang = 'es' }: RegulatoryNoticePageProps) {
  const t = STRINGS[lang];

  const sections: Array<{ title: string; body: string[]; highlight?: boolean }> = [
    { title: t.s1Title, body: t.s1Body },
    { title: t.s2Title, body: t.s2Body },
    { title: t.s3Title, body: t.s3Body },
    { title: t.s4Title, body: t.s4Body },
    { title: t.s5Title, body: t.s5Body },
    { title: t.s6Title, body: t.s6Body, highlight: true },
    { title: t.s7Title, body: t.s7Body },
    { title: t.s8Title, body: t.s8Body },
  ];

  return (
    <div style={{ background: BG, color: TEXT, minHeight: '100vh' }}>
      <FontImport />
      <PublicNav lang={lang} />

      <main
        style={{
          padding: '96px 0 64px',
        }}
      >
        <article
          className="mx-auto px-6 lg:px-12"
          style={{ maxWidth: 760 }}
        >
          {/* Header */}
          <header
            style={{
              paddingBottom: 48,
              marginBottom: 56,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <div
              className="font-mono text-xs uppercase tracking-widest"
              style={{ color: PRIMARY, marginBottom: 16 }}
            >
              {t.pageEyebrow}
            </div>
            <h1
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontWeight: 400,
                fontSize: 'clamp(2.25rem, 5vw, 3.25rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.015em',
                color: TEXT,
                margin: '0 0 24px',
              }}
            >
              {t.pageTitle}
            </h1>
            <p
              style={{
                fontFamily: "'Instrument Sans', system-ui, sans-serif",
                fontSize: '1.125rem',
                lineHeight: 1.6,
                color: TEXT_DIM,
                margin: '0 0 20px',
                maxWidth: '60ch',
              }}
            >
              {t.pageSubtitle}
            </p>
            <p
              className="font-mono text-xs"
              style={{ color: TEXT_FAINT, margin: 0 }}
            >
              {t.pageMeta}
            </p>
          </header>

          {/* Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>
            {sections.map((sec, i) => (
              <section
                key={i}
                style={
                  sec.highlight
                    ? {
                        background: BG_CARD,
                        border: `1px solid ${PRIMARY}33`,
                        borderRadius: 4,
                        padding: '32px',
                      }
                    : undefined
                }
              >
                <h2
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontWeight: 500,
                    fontSize: '1.5rem',
                    lineHeight: 1.25,
                    letterSpacing: '-0.005em',
                    color: sec.highlight ? PRIMARY : TEXT,
                    margin: '0 0 20px',
                  }}
                >
                  {sec.title}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {sec.body.map((p, j) => (
                    <p
                      key={j}
                      style={{
                        fontFamily: "'Instrument Sans', system-ui, sans-serif",
                        fontSize: '1rem',
                        lineHeight: 1.7,
                        color: TEXT_DIM,
                        margin: 0,
                      }}
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Footer meta */}
          <footer
            style={{
              marginTop: 80,
              paddingTop: 32,
              borderTop: `1px solid ${BORDER}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <p
              className="font-mono text-xs"
              style={{
                color: TEXT_FAINT,
                margin: 0,
                fontStyle: 'italic',
                maxWidth: '50ch',
              }}
            >
              {t.docMeta}
            </p>
            <Link
              to={lang === 'en' ? '/en' : '/'}
              className="font-mono text-xs uppercase tracking-wider"
              style={{ color: PRIMARY, textDecoration: 'none' }}
            >
              ← {t.backHome}
            </Link>
          </footer>
        </article>
      </main>

      <PublicFooter lang={lang} />
    </div>
  );
}
